import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MdOutlineWatchLater,
  MdPlayArrow,
  MdOutlinePlaylistPlay,
  MdOutlineThumbUp,
  MdAdd,
  MdClose,
  MdMoreVert,
  MdLockOutline,
  MdPublic,
  MdLink,
  MdOutlineDelete,
  MdOutlineShare,
  MdPlaylistAdd,
  MdKeyboardArrowDown,
  MdCheck,
  MdShuffle,
} from 'react-icons/md';
import { RiPlayList2Line, RiPlayList2Fill } from 'react-icons/ri';
import Toast from './Toast';
import { getActiveAccount } from './utils/accountUtils';
import { API_BASE_URL, normalizeMediaUrl } from './config';
import './PlaylistsPage.css';

const DEFAULT_FALLBACK_THUMBNAIL = normalizeMediaUrl('/drive/video_thumbnails/thumbnail-157.jpeg') || `${process.env.PUBLIC_URL || ''}/img/banner.png`;

const SORT_OPTIONS = [
  { id: 'recent', label: 'Recently added' },
  { id: 'name_asc', label: 'A-Z' },
  { id: 'name_desc', label: 'Z-A' },
];

export default function PlaylistsPage({ userProfile, onNavigate }) {
  const [playlists, setPlaylists] = useState([]);
  const [watchLaterCount, setWatchLaterCount] = useState(0);
  const [watchLaterThumb, setWatchLaterThumb] = useState(DEFAULT_FALLBACK_THUMBNAIL);
  const [likedVideosCount, setLikedVideosCount] = useState(0);
  const [likedThumb, setLikedThumb] = useState(DEFAULT_FALLBACK_THUMBNAIL);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'name_asc', 'name_desc'
  const [activeFilter, setActiveFilter] = useState('All'); // 'All', 'Mixes', 'Created playlists', 'Saved'
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef(null);

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistVisibility, setNewPlaylistVisibility] = useState('PUBLIC');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);

  // Toast State
  const [toast, setToast] = useState({ isOpen: false, message: '', icon: null });
  const menuRef = useRef(null);

  const showToast = (message, icon = null) => {
    setToast({ isOpen: false, message: '', icon: null });
    setTimeout(() => {
      setToast({ isOpen: true, message, icon });
    }, 50);
  };

  // Close context menu & sort dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setActiveMenuId(null);
      }
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setIsSortOpen(false);
      }
    };
    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch Watch Later count and thumbnail
  const fetchWatchLaterInfo = useCallback(async () => {
    try {
      const token = localStorage.getItem('speedotube_user_token') || getActiveAccount()?.token;
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/api/v1/watch-later/users/me`, {
        headers: {
          devicetype: '0',
          token: 'c7e993808a344da9d59cfa20fa8b6061',
          request_type: 'USER',
          authorization: `Bearer ${token}`,
        },
      });
      let count = 0;
      let thumb = DEFAULT_FALLBACK_THUMBNAIL;

      if (res.ok) {
        const json = await res.json();
        const list = json?.data?.watchLater || [];
        count = list.length;
        if (list.length > 0 && list[0].thumbnail_url) {
          thumb = normalizeMediaUrl(list[0].thumbnail_url);
        }
      }

      // Check local storage items as well
      const savedLocal = localStorage.getItem('speedotube_watch_later_list');
      if (savedLocal) {
        try {
          const localList = JSON.parse(savedLocal);
          if (localList.length > count) {
            count = localList.length;
            if (localList[0].thumbnailUrl) thumb = localList[0].thumbnailUrl;
          }
        } catch (e) {}
      }

      setWatchLaterCount(count);
      setWatchLaterThumb(thumb);
    } catch (e) {
      console.warn('Watch later count fetch error:', e);
    }
  }, []);

  // Fetch Liked Videos count and thumbnail
  const fetchLikedVideosInfo = useCallback(async () => {
    try {
      const token = localStorage.getItem('speedotube_user_token') || getActiveAccount()?.token;
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/api/v1/liked-videos/users/me`, {
        headers: {
          devicetype: '0',
          token: 'c7e993808a344da9d59cfa20fa8b6061',
          request_type: 'USER',
          authorization: `Bearer ${token}`,
        },
      });
      let count = 0;
      let thumb = DEFAULT_FALLBACK_THUMBNAIL;

      if (res.ok) {
        const json = await res.json();
        const list = json?.data?.likedVideos || [];
        count = list.length;
        if (list.length > 0 && list[0].thumbnail_url) {
          thumb = normalizeMediaUrl(list[0].thumbnail_url);
        }
      }

      // Check local storage items as well
      const savedLiked = localStorage.getItem('speedotube_liked_videos');
      if (savedLiked) {
        try {
          const localList = JSON.parse(savedLiked);
          if (Array.isArray(localList)) {
            if (localList.length > 0) {
              if (localList.length > count) count = localList.length;
              if (localList[0]?.thumbnailUrl) thumb = localList[0].thumbnailUrl;
            } else if (count === 0) {
              count = 0;
            }
          }
        } catch (e) {}
      }

      setLikedVideosCount(count);
      setLikedThumb(thumb);
    } catch (e) {
      console.warn('Liked videos count fetch error:', e);
      setLikedVideosCount(0);
    }
  }, []);

  // Fetch all custom playlists from backend & localStorage
  const fetchAllPlaylists = useCallback(async () => {
    setIsLoading(true);
    let backendPlaylists = [];

    try {
      const token = localStorage.getItem('speedotube_user_token') || getActiveAccount()?.token;
      if (token) {
        const res = await fetch(`${API_BASE_URL}/api/v1/all-playlists/users/me`, {
          headers: {
            devicetype: '0',
            token: 'c7e993808a344da9d59cfa20fa8b6061',
            request_type: 'USER',
            authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const json = await res.json();
          const list = json?.data?.playlists || [];
          backendPlaylists = list.map((p) => ({
            id: String(p.id),
            name: p.name || 'Untitled Playlist',
            visibility: p.visibility || 'PUBLIC',
            videoCount: p.video_count || p.total_videos || 0,
            thumbnailUrl: normalizeMediaUrl(p.thumbnail_url) || DEFAULT_FALLBACK_THUMBNAIL,
            updatedDate: p.modified_date || p.created_date || new Date().toISOString(),
            isCustom: true,
          }));
        }
      }
    } catch (e) {
      console.warn('Backend playlist fetch error:', e);
    }

    // Merge with locally created playlists
    try {
      const localSaved = localStorage.getItem('speedotube_custom_playlists');
      const localList = localSaved ? JSON.parse(localSaved) : [];

      const map = new Map();
      backendPlaylists.forEach((p) => map.set(String(p.id), p));
      localList.forEach((p) => {
        if (!map.has(String(p.id))) {
          map.set(String(p.id), p);
        }
      });

      setPlaylists(Array.from(map.values()));
    } catch (e) {
      setPlaylists(backendPlaylists);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWatchLaterInfo();
    fetchLikedVideosInfo();
    fetchAllPlaylists();

    const handleRefresh = () => {
      fetchWatchLaterInfo();
      fetchLikedVideosInfo();
      fetchAllPlaylists();
    };

    window.addEventListener('speedotube_playlist_created', handleRefresh);
    window.addEventListener('speedotube_watch_later_updated', handleRefresh);
    window.addEventListener('speedotube_liked_videos_updated', handleRefresh);
    window.addEventListener('storage', handleRefresh);
    return () => {
      window.removeEventListener('speedotube_playlist_created', handleRefresh);
      window.removeEventListener('speedotube_watch_later_updated', handleRefresh);
      window.removeEventListener('speedotube_liked_videos_updated', handleRefresh);
      window.removeEventListener('storage', handleRefresh);
    };
  }, [fetchWatchLaterInfo, fetchLikedVideosInfo, fetchAllPlaylists]);

  // Handle Create Playlist
  const handleCreatePlaylist = async (e) => {
    e.preventDefault();
    if (!newPlaylistName.trim()) return;

    setIsSubmitting(true);
    const tempId = `playlist_${Date.now()}`;
    const newPlaylist = {
      id: tempId,
      name: newPlaylistName.trim(),
      visibility: newPlaylistVisibility,
      videoCount: 0,
      thumbnailUrl: DEFAULT_FALLBACK_THUMBNAIL,
      updatedDate: new Date().toISOString(),
      isCustom: true,
    };

    // 1. Optimistic Local Update
    setPlaylists((prev) => [newPlaylist, ...prev]);
    try {
      const localSaved = localStorage.getItem('speedotube_custom_playlists');
      const list = localSaved ? JSON.parse(localSaved) : [];
      list.unshift(newPlaylist);
      localStorage.setItem('speedotube_custom_playlists', JSON.stringify(list));
    } catch (err) {
      console.warn('Local playlist save error:', err);
    }

    // 2. Call Backend API
    try {
      const token = localStorage.getItem('speedotube_user_token') || getActiveAccount()?.token;
      if (!token) return;
      const res = await fetch(`${API_BASE_URL}/api/v1/playlists`, {
        method: 'POST',
        headers: {
          devicetype: '0',
          token: 'c7e993808a344da9d59cfa20fa8b6061',
          request_type: 'USER',
          authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newPlaylistName.trim(),
          visibility: newPlaylistVisibility,
          video_id: 157, // Default anchor video for creation
        }),
      });

      if (res.ok) {
        const json = await res.json();
        if (json?.data?.id) {
          // Replace temp ID with backend ID
          newPlaylist.id = String(json.data.id);
        }
      }
    } catch (err) {
      console.warn('Backend playlist creation error (handled gracefully):', err);
    } finally {
      setIsSubmitting(false);
      setIsCreateModalOpen(false);
      setNewPlaylistName('');
      showToast('Playlist created');
      window.dispatchEvent(new Event('speedotube_playlist_created'));
    }
  };

  // Delete Playlist
  const handleDeletePlaylist = async (playlistId, e) => {
    e.stopPropagation();
    setActiveMenuId(null);

    // 1. Local update
    setPlaylists((prev) => prev.filter((p) => String(p.id) !== String(playlistId)));
    try {
      const localSaved = localStorage.getItem('speedotube_custom_playlists');
      const list = localSaved ? JSON.parse(localSaved) : [];
      const filtered = list.filter((p) => String(p.id) !== String(playlistId));
      localStorage.setItem('speedotube_custom_playlists', JSON.stringify(filtered));
    } catch (err) {}

    showToast('Playlist deleted');

    // 2. Backend call
    try {
      const token = localStorage.getItem('speedotube_user_token') || getActiveAccount()?.token;
      if (!token) return;
      await fetch(`${API_BASE_URL}/api/v1/playlists/${playlistId}`, {
        method: 'DELETE',
        headers: {
          devicetype: '0',
          token: 'c7e993808a344da9d59cfa20fa8b6061',
          request_type: 'USER',
          authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.warn('Backend playlist deletion error:', err);
    }
  };

  // Sort Custom Playlists
  const sortedPlaylists = [...playlists].sort((a, b) => {
    if (sortBy === 'name_asc') return a.name.localeCompare(b.name);
    if (sortBy === 'name_desc') return b.name.localeCompare(a.name);
    return new Date(b.updatedDate || 0) - new Date(a.updatedDate || 0);
  });

  const currentAcc = userProfile || getActiveAccount();

  const totalPlaylistsCount =
    (watchLaterCount > 0 ? 1 : 0) + (likedVideosCount > 0 ? 1 : 0) + sortedPlaylists.length;

  return (
    <div className="yt-playlists-page">
      {/* Page Header (Matches user design with bold title & actions) */}
      <div className="yt-playlists-header">
        <div className="yt-playlists-title-group">
          <h1 className="yt-playlists-main-title">Playlists</h1>
        </div>

        <div className="yt-playlists-actions-bar">
          {/* Custom Smooth Sort Selector */}
          <div className="yt-playlists-sort-wrap" ref={sortRef}>
            <span className="yt-playlists-sort-label">Sort by:</span>
            <button
              type="button"
              className={`yt-playlists-sort-trigger ${isSortOpen ? 'open' : ''}`}
              onClick={() => setIsSortOpen((prev) => !prev)}
              aria-haspopup="listbox"
              aria-expanded={isSortOpen}
            >
              <span className="yt-playlists-sort-value">
                {SORT_OPTIONS.find((o) => o.id === sortBy)?.label || 'Recently added'}
              </span>
              <MdKeyboardArrowDown
                size={18}
                className={`yt-playlists-sort-chevron ${isSortOpen ? 'rotated' : ''}`}
              />
            </button>

            {isSortOpen && (
              <div className="yt-playlists-sort-dropdown-menu">
                {SORT_OPTIONS.map((opt) => {
                  const isSelected = sortBy === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={`yt-playlists-sort-item ${isSelected ? 'active' : ''}`}
                      onClick={() => {
                        setSortBy(opt.id);
                        setIsSortOpen(false);
                      }}
                    >
                      <span className="yt-playlists-sort-check">
                        {isSelected && <MdCheck size={16} />}
                      </span>
                      <span className="yt-playlists-sort-item-label">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* New Playlist Action Button (Compact pill with snug "+" icon) */}
          <button
            className="yt-playlists-create-btn"
            onClick={() => setIsCreateModalOpen(true)}
            title="Create new playlist"
          >
            <MdAdd size={18} className="yt-playlists-add-icon" />
            <span>New playlist</span>
          </button>
        </div>
      </div>

      {/* Playlists Grid (When playlists exist) */}
      {totalPlaylistsCount > 0 && (
        <div className="yt-playlists-grid">
          {/* Method 1: Watch Later (Only shown if user has saved videos; disappears when unsaved) */}
          {watchLaterCount > 0 && (
            <div
              className="yt-playlist-card"
              onClick={() => onNavigate && onNavigate('/watch-later')}
            >
              <div className="yt-playlist-thumb-wrap">
                <div className="yt-playlist-stack-layer" />
                <img
                  src={watchLaterThumb || DEFAULT_FALLBACK_THUMBNAIL}
                  alt="Watch Later"
                  className="yt-playlist-thumb-img"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = DEFAULT_FALLBACK_THUMBNAIL;
                  }}
                />
                <div className="yt-playlist-badge">
                  <MdOutlineWatchLater size={15} />
                  <span>{watchLaterCount} {watchLaterCount === 1 ? 'video' : 'videos'}</span>
                </div>
                <div className="yt-playlist-hover-play">
                  <MdPlayArrow size={34} />
                  <span>PLAY ALL</span>
                </div>
              </div>

              <div className="yt-playlist-info">
                <div className="yt-playlist-title-row">
                  <h3 className="yt-playlist-name" title="Watch Later">
                    Watch Later
                  </h3>
                </div>
                <p className="yt-playlist-meta">
                  <span className="yt-playlist-owner">{currentAcc?.name || 'User'}</span>
                  <span className="yt-bullet">•</span>
                  <span className="yt-playlist-privacy">
                    <MdLockOutline size={12} /> Private
                  </span>
                </p>
                <button
                  className="yt-playlist-view-all-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onNavigate) onNavigate('/watch-later');
                  }}
                >
                  View full playlist
                </button>
              </div>
            </div>
          )}

          {/* Method 1: Liked videos (Only shown if user has liked videos; disappears when unliked) */}
          {likedVideosCount > 0 && (
            <div
              className="yt-playlist-card"
              onClick={() => onNavigate && onNavigate('/liked-videos')}
            >
              <div className="yt-playlist-thumb-wrap">
                <div className="yt-playlist-stack-layer" />
                <img
                  src={likedThumb || DEFAULT_FALLBACK_THUMBNAIL}
                  alt="Liked videos"
                  className="yt-playlist-thumb-img"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = DEFAULT_FALLBACK_THUMBNAIL;
                  }}
                />
                <div className="yt-playlist-badge">
                  <MdOutlineThumbUp size={14} />
                  <span>{likedVideosCount} {likedVideosCount === 1 ? 'video' : 'videos'}</span>
                </div>
                <div className="yt-playlist-hover-play">
                  <MdPlayArrow size={34} />
                  <span>PLAY ALL</span>
                </div>
              </div>

              <div className="yt-playlist-info">
                <div className="yt-playlist-title-row">
                  <h3 className="yt-playlist-name" title="Liked videos">
                    Liked videos
                  </h3>
                </div>
                <p className="yt-playlist-meta">
                  <span className="yt-playlist-owner">{currentAcc?.name || 'User'}</span>
                  <span className="yt-bullet">•</span>
                  <span className="yt-playlist-privacy">
                    <MdLockOutline size={12} /> Auto playlist
                  </span>
                </p>
                <button
                  className="yt-playlist-view-all-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onNavigate) onNavigate('/liked-videos');
                  }}
                >
                  View full playlist
                </button>
              </div>
            </div>
          )}

          {/* Method 2: Manually Created User Playlists */}
          {sortedPlaylists.map((pl) => (
            <div
              key={pl.id}
              className="yt-playlist-card"
              onClick={() => onNavigate && onNavigate('/watch?v=HyRyzOCHONj')}
            >
              <div className="yt-playlist-thumb-wrap">
                <div className="yt-playlist-stack-layer" />
                <img
                  src={pl.thumbnailUrl || DEFAULT_FALLBACK_THUMBNAIL}
                  alt={pl.name}
                  className="yt-playlist-thumb-img"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = DEFAULT_FALLBACK_THUMBNAIL;
                  }}
                />
                <div className="yt-playlist-badge">
                  <RiPlayList2Fill size={14} />
                  <span>{pl.videoCount} {pl.videoCount === 1 ? 'video' : 'videos'}</span>
                </div>
                <div className="yt-playlist-hover-play">
                  <MdPlayArrow size={34} />
                  <span>PLAY ALL</span>
                </div>
              </div>

              <div className="yt-playlist-info">
                <div className="yt-playlist-title-row">
                  <h3 className="yt-playlist-name" title={pl.name}>
                    {pl.name}
                  </h3>
                  <div className="yt-playlist-menu-wrap">
                    <button
                      className="yt-playlist-menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenuId(activeMenuId === pl.id ? null : pl.id);
                      }}
                      aria-label="Playlist actions"
                    >
                      <MdMoreVert size={18} />
                    </button>

                    {activeMenuId === pl.id && (
                      <div className="yt-playlist-dropdown-menu" ref={menuRef}>
                        <button
                          className="yt-playlist-dropdown-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenuId(null);
                            if (onNavigate) onNavigate('/watch?v=HyRyzOCHONj');
                          }}
                        >
                          <MdPlayArrow size={18} />
                          <span>Play all</span>
                        </button>
                        <button
                          className="yt-playlist-dropdown-item delete"
                          onClick={(e) => handleDeletePlaylist(pl.id, e)}
                        >
                          <MdOutlineDelete size={18} />
                          <span>Delete playlist</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <p className="yt-playlist-meta">
                  <span className="yt-playlist-owner">{currentAcc?.name || 'User'}</span>
                  <span className="yt-bullet">•</span>
                  <span className="yt-playlist-privacy">
                    {pl.visibility === 'PRIVATE' ? (
                      <>
                        <MdLockOutline size={12} /> Private
                      </>
                    ) : pl.visibility === 'UNLISTED' ? (
                      <>
                        <MdLink size={12} /> Unlisted
                      </>
                    ) : (
                      <>
                        <MdPublic size={12} /> Public
                      </>
                    )}
                  </span>
                </p>

                <button
                  className="yt-playlist-view-all-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onNavigate) onNavigate('/watch?v=HyRyzOCHONj');
                  }}
                >
                  View full playlist
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Exact Empty State matching user reference image media_1790406990689.png */}
      {totalPlaylistsCount === 0 && !isLoading && (
        <div className="yt-playlists-empty-state">
          <div className="yt-playlists-empty-icon-box">
            <RiPlayList2Line size={48} className="yt-playlists-empty-icon" />
          </div>
          <h3 className="yt-playlists-empty-title">No playlists yet</h3>
          <p className="yt-playlists-empty-desc">
            Playlists you create will show up here.
          </p>
          <button
            className="yt-playlists-empty-create-btn"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <MdAdd size={20} />
            <span>Create playlist</span>
          </button>
        </div>
      )}

      {/* Modal Dialog: Create New Playlist */}
      {isCreateModalOpen && (
        <div
          className="yt-playlist-modal-overlay"
          onClick={() => !isSubmitting && setIsCreateModalOpen(false)}
        >
          <div
            className="yt-playlist-modal-container"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="yt-playlist-modal-header">
              <h2 className="yt-playlist-modal-title">New playlist</h2>
              <button
                className="yt-playlist-modal-close-btn"
                onClick={() => setIsCreateModalOpen(false)}
                aria-label="Close"
              >
                <MdClose size={22} />
              </button>
            </div>

            <form onSubmit={handleCreatePlaylist}>
              <div className="yt-playlist-modal-body">
                {/* Title Input */}
                <div className="yt-playlist-input-field">
                  <label htmlFor="pl-name-input" className="yt-playlist-field-label">
                    Title
                  </label>
                  <input
                    id="pl-name-input"
                    type="text"
                    className="yt-playlist-text-input"
                    placeholder="Enter playlist title..."
                    value={newPlaylistName}
                    onChange={(e) => setNewPlaylistName(e.target.value)}
                    maxLength={150}
                    autoFocus
                    required
                  />
                  <span className="yt-playlist-field-count">
                    {newPlaylistName.length}/150
                  </span>
                </div>

                {/* Privacy Selection */}
                <div className="yt-playlist-input-field">
                  <label htmlFor="pl-privacy-select" className="yt-playlist-field-label">
                    Privacy
                  </label>
                  <select
                    id="pl-privacy-select"
                    className="yt-playlist-select-input"
                    value={newPlaylistVisibility}
                    onChange={(e) => setNewPlaylistVisibility(e.target.value)}
                  >
                    <option value="PUBLIC">Public</option>
                    <option value="UNLISTED">Unlisted</option>
                    <option value="PRIVATE">Private</option>
                  </select>
                  <span className="yt-playlist-field-hint">
                    {newPlaylistVisibility === 'PUBLIC'
                      ? 'Anyone can search for and view'
                      : newPlaylistVisibility === 'UNLISTED'
                      ? 'Anyone with the link can view'
                      : 'Only you can view'}
                  </span>
                </div>
              </div>

              <div className="yt-playlist-modal-footer">
                <button
                  type="button"
                  className="yt-playlist-modal-cancel-btn"
                  onClick={() => setIsCreateModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newPlaylistName.trim() || isSubmitting}
                  className={`yt-playlist-modal-submit-btn ${
                    newPlaylistName.trim() && !isSubmitting ? 'active' : ''
                  }`}
                >
                  {isSubmitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        icon={toast.icon}
        onClose={() => setToast({ isOpen: false, message: '', icon: null })}
      />
    </div>
  );
}
